import { useState, useEffect, useCallback } from 'react';
import { isLockedCategory, isAdminMode } from '../constants.js';

// Hook for managing navigation state with PROPER subcategory ordering from CMS
// NOW WITH COMPULSORY NAVIGATION BLOCKING
export const useNavigation = (mainCategories, productData, categoryConfig, shouldBlockNavigation = null, onNavigationBlocked = null) => {
  const [selectedMainCategory, setSelectedMainCategory] = useState('');
  const [selectedSubCategory, setSelectedSubCategory] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(0);
  const [viewState, setViewState] = useState('main');
  const [showCompulsoryPopup, setShowCompulsoryPopup] = useState(false);

  // Check if a main category has real subcategories
  const hasRealSubCategories = useCallback((mainCategoryName) => {
    const subCategories = Object.keys(productData[mainCategoryName] || {});
    return subCategories.length > 1 || (subCategories.length === 1 && subCategories[0] !== 'General');
  }, [productData]);

  // USE CMS SUBCATEGORY ORDERING - This is the key fix!
  const getCurrentSubCategories = useCallback(() => {
    if (!selectedMainCategory || !productData[selectedMainCategory]) {
      return [];
    }
    
    // Get actual subcategories that exist in product data
    const actualSubCategories = Object.keys(productData[selectedMainCategory]);
    
    // Check if CMS config has subcategoryOrder for this category
    const categoryConfigData = categoryConfig[selectedMainCategory];

    if (categoryConfigData?.subcategoryOrder && Array.isArray(categoryConfigData.subcategoryOrder) && categoryConfigData.subcategoryOrder.length > 0) {
      // Filter CMS order to only include subcategories that actually exist in product data
      const orderedSubcategories = categoryConfigData.subcategoryOrder.filter(subcat =>
        actualSubCategories.includes(subcat)
      );

      // Add any actual subcategories that aren't in the CMS order (as fallback)
      const missingSubcategories = actualSubCategories.filter(subcat =>
        !categoryConfigData.subcategoryOrder.includes(subcat)
      );

      return [...orderedSubcategories, ...missingSubcategories.sort()];
    }
    
    // Fallback to alphabetical sorting if no CMS order
    return actualSubCategories.sort();
  }, [selectedMainCategory, productData, categoryConfig]);

  // Handle main category selection with proper subcategory auto-setting
  // NOW CHECKS FOR COMPULSORY BLOCKING
  const handleMainCategoryClick = useCallback((mainCategoryName) => {
    if (mainCategoryName && typeof mainCategoryName === 'string') {
      // CHECK: Should navigation be blocked?
      if (shouldBlockNavigation && shouldBlockNavigation(mainCategoryName, '')) {
        setShowCompulsoryPopup(true); // SHOW THE POPUP!
        if (onNavigationBlocked) onNavigationBlocked();
        return; // Don't allow navigation
      }

      setSelectedMainCategory(mainCategoryName);
      setViewState('sub');
      setSelectedProduct(0);
      
      // Set first subcategory using CMS ordering
      if (productData[mainCategoryName]) {
        const actualSubCategories = Object.keys(productData[mainCategoryName]);
        let firstSubCategory = '';
        
        // Check CMS config for this category
        const categoryConfigData = categoryConfig[mainCategoryName];
        
        if (categoryConfigData?.subcategoryOrder && Array.isArray(categoryConfigData.subcategoryOrder) && categoryConfigData.subcategoryOrder.length > 0) {
          // Filter to existing subcategories and take the first one
          const filteredCmsOrder = categoryConfigData.subcategoryOrder.filter(subcat => 
            actualSubCategories.includes(subcat)
          );
          
          if (filteredCmsOrder.length > 0) {
            firstSubCategory = filteredCmsOrder[0];
          } else {
            firstSubCategory = actualSubCategories.sort()[0];
          }
        } else {
          // Fallback to alphabetical
          firstSubCategory = actualSubCategories.sort()[0];
        }
        
        setSelectedSubCategory(firstSubCategory);
      } else {
        setSelectedSubCategory('');
      }
    }
  }, [productData, categoryConfig, shouldBlockNavigation, onNavigationBlocked]);

  // Handle subcategory selection
  // NOW CHECKS FOR COMPULSORY BLOCKING
  const handleSubCategoryClick = useCallback((subCategoryName) => {
    if (subCategoryName && typeof subCategoryName === 'string') {
      // CHECK: Should navigation be blocked?
      if (shouldBlockNavigation && shouldBlockNavigation(selectedMainCategory, subCategoryName)) {
        setShowCompulsoryPopup(true); // SHOW THE POPUP!
        if (onNavigationBlocked) onNavigationBlocked();
        return; // Don't allow navigation
      }

      setSelectedSubCategory(subCategoryName);
      setSelectedProduct(0);
    }
  }, [selectedMainCategory, shouldBlockNavigation, onNavigationBlocked]);

  // Handle back navigation - always go back to main categories
  // NOW CHECKS FOR COMPULSORY BLOCKING
  const handleBack = useCallback(() => {
    // CHECK: Should navigation be blocked?
    if (shouldBlockNavigation && shouldBlockNavigation('', '')) {
      setShowCompulsoryPopup(true); // SHOW THE POPUP!
      if (onNavigationBlocked) onNavigationBlocked();
      return; // Don't allow going back
    }

    setViewState('main');
    setSelectedMainCategory(''); // Clear main category selection when going back
    setSelectedSubCategory('');
    setSelectedProduct(0);
  }, [shouldBlockNavigation, onNavigationBlocked]);

  // Close compulsory popup
  const closeCompulsoryPopup = useCallback(() => {
    setShowCompulsoryPopup(false);
  }, []);

  // UPDATED: Filter out locked categories - but allow chassis in admin mode
  const getSelectableCategories = useCallback((categories) => {
    if (isAdminMode()) {
      // In admin mode, show ALL categories including chassis
      return categories;
    } else {
      // For regular users, filter out locked categories (chassis)
      return categories.filter(cat => !isLockedCategory(cat));
    }
  }, []);

  // No auto-selection of first category - let user choose

  // Reset selected product when products change or when it's out of bounds
  useEffect(() => {
    const products = productData[selectedMainCategory]?.[selectedSubCategory] || [];
    if (selectedProduct >= products.length) {
      setSelectedProduct(0);
    }
  }, [selectedMainCategory, selectedSubCategory, productData, selectedProduct]);

  return {
    selectedMainCategory,
    selectedSubCategory,
    selectedProduct,
    viewState,
    showCompulsoryPopup,
    setSelectedProduct,
    setSelectedMainCategory,
    setSelectedSubCategory,
    setViewState,
    hasRealSubCategories,
    getCurrentSubCategories,
    handleMainCategoryClick,
    handleSubCategoryClick,
    handleBack,
    getSelectableCategories,
    closeCompulsoryPopup
  };
};